import { Button } from '@app/components/button/button';
import { BranchStartIcon } from '@app/components/icons/branch-start-icon';
import { SortAscIcon } from '@app/components/icons/sort-ascending';
import { SortDescIcon } from '@app/components/icons/sort-descending';
import { useTranslation } from '@hopin-team/provider-translation';
import { useInfiniteQuery } from '@tanstack/react-query';
import cx from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Arrow, useLayer } from 'react-laag';
import { useThrottledCallback } from 'use-debounce';

import { InterContentStyles, InterHeaderStyles } from './constants';

interface THProps {
  width?: string;
  sort?: boolean;
  children: React.ReactNode;
}

const TH: React.FC<THProps> = ({ sort, children }) => {
  return (
    <th
      className={cx('py-4 pr-6 pl-2 text-left', InterHeaderStyles, {
        'cursor-pointer': sort,
        'cursor-default': !sort,
      })}
    >
      {children}
    </th>
  );
};

export interface TemplateProps<T> {
  value: T[keyof T];
  data: T;
  column: Column<T>;
  rowIndex: number;
}

export interface Column<T> {
  name?: keyof T;
  text: string;
  customCSS?: string;
  width?: string;
  sort?: boolean;
  formatter?: (value: string, rowData?: T) => string;
  template?: React.FC<TemplateProps<T>>;
}

const DataCell = <T,>({
  rowData,
  column,
  colIndex,
  rowIndex,
  isChild = false,
  onToggleExpand,
}: {
  rowData: T;
  column: Column<T>;
  colIndex: number;
  rowIndex: number;
  isChild?: boolean;
  onToggleExpand?: (row: T) => void;
}) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const cellRef = useRef<HTMLTableCellElement | null>(null);
  const Template = column.template;

  const content = column.formatter
    ? column.formatter(String(rowData[column.name as keyof T]), rowData)
    : rowData[column.name as keyof T];

  useEffect(() => {
    const el = cellRef.current;

    if (el) {
      const checkOverflow = () => {
        setIsOverflowed(el.scrollWidth > el.clientWidth);
      };

      checkOverflow(); // Initial check

      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(el);

      return () => resizeObserver.disconnect();
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [column.name, content]);

  // Setup layer for tooltip using react-laag
  const { layerProps, arrowProps, triggerProps } = useLayer({
    isOpen: showTooltip,
    placement: 'top-center',
    triggerOffset: 10,
    onOutsideClick: () => setShowTooltip(false),
  });

  const handleMouseEnter = () => {
    if (isOverflowed) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <td
      key={colIndex}
      className={cx(
        'p-2 pr-6 pl-2 relative',
        column.customCSS ? column.customCSS : InterContentStyles,
        isChild && colIndex === 0 && 'overflow-visible',
      )}
      style={{
        ...(isChild && colIndex === 0 && { overflow: 'visible' }),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...triggerProps}
    >
      <div
        className={cx(
          'truncate max-w-full',
          isChild && colIndex === 0 && 'overflow-visible',
        )}
        ref={cellRef}
        onClick={() => {
          if (colIndex === 0 && onToggleExpand && !isChild) {
            onToggleExpand(rowData);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (colIndex === 0 && onToggleExpand && !isChild) {
              onToggleExpand(rowData);
            }
          }
        }}
        role="button"
        tabIndex={0}
        style={{
          cursor:
            colIndex === 0 &&
            onToggleExpand &&
            !isChild &&
            'eventsCount' in (rowData as object) &&
            (rowData as { eventsCount?: number }).eventsCount
              ? 'pointer'
              : 'default',
          ...(isChild && colIndex === 0 && { overflow: 'visible' }),
        }}
      >
        {Template ? (
          <Template
            value={rowData[column.name as keyof T]}
            data={rowData}
            column={column}
            rowIndex={rowIndex}
          />
        ) : (
          <>
            {column.formatter
              ? column.formatter(
                  String(rowData[column.name as keyof T]),
                  rowData,
                )
              : rowData[column.name as keyof T] !== null
              ? rowData[column.name as keyof T]
              : '-'}
          </>
        )}

        {showTooltip && (
          <div
            {...layerProps}
            className="bg-gray-800 text-white p-2 rounded max-w-full break-words z-50"
          >
            {content}
            <Arrow {...arrowProps} backgroundColor="black" />
          </div>
        )}
      </div>
    </td>
  );
};

const DataRow = <T,>({
  rowData,
  columns,
  rowIndex,
  isChild = false,
  onToggleExpand,
}: {
  rowData: T;
  columns: Column<T>[];
  rowIndex: number;
  isChild?: boolean;
  onToggleExpand?: (row: T) => void;
  isExpanded?: boolean;
}) => {
  return (
    <tr className="my-2 hover:bg-gray-100">
      {columns.map((column, colIndex) => {
        return (
          <DataCell
            key={colIndex}
            rowData={rowData}
            rowIndex={rowIndex}
            column={column}
            colIndex={colIndex}
            isChild={isChild}
            onToggleExpand={onToggleExpand}
          />
        );
      })}
    </tr>
  );
};

// When last row is in viewport, it will start fetching more data
const LastDataRow = React.forwardRef<
  HTMLTableRowElement,
  {
    rowData: any;
    columns: Column<any>[];
    rowIndex: number;
    isChild?: boolean;
  }
>(({ rowData, columns, rowIndex, isChild = false }, ref) => {
  return (
    <tr ref={ref} className={cx('my-2 hover:bg-gray-100')}>
      {columns.map((column, colIndex) => {
        return (
          <DataCell
            key={colIndex}
            rowData={rowData}
            rowIndex={rowIndex}
            column={column}
            colIndex={colIndex}
            isChild={isChild}
          />
        );
      })}
    </tr>
  );
});

LastDataRow.displayName = 'LastDataRow';

export interface DataGridProps<T, C = T> {
  columns: Column<T>[];
  data: T[];
  onSort?: (columnKey: keyof T, direction: 'asc' | 'desc' | '') => void;
  showLoader: boolean;
  loader: React.FC<{ count: number }>;
  loaderCount: number;
  fetchMore?: () => void;
  sortConfig: { name: keyof T; direction: 'asc' | 'desc' | '' };
  noResults: () => React.ReactNode;
  error: React.FC;
  isError: boolean;
  hierarchyId?: string;
  fetchChildRows?: (parentRow: T) => Promise<C[]>;
  fetchMoreChildRows?: (parentRow: T, currentPage: number) => Promise<C[]>;
  childColumns?: Column<C>[];
  childLoader?: React.FC<{ count: number }>;
  renderChildRow?: (child: T, parent: T) => React.ReactNode;
  onExpandToggle?: (row: T) => void;
  getChildLoaderCount?: (rowData: T, currentPage: number) => number;
  childGridPageSize?: number;
  disableTriStateSort?: boolean;
  onExpandCollapse?: (row: T, isExpanded: boolean) => void;
}

const ChildRowRenderer = <T, C = T>({
  parentRow,
  rowIndex,
  columns,
  childColumns,
  childLoader,
  fetchChildRows,
  fetchMoreChildRows,
  renderChildRow,
  getChildLoaderCount,
  hierarchyId,
  childGridPageSize,
}: {
  parentRow: T;
  rowIndex: number;
  columns: Column<T>[];
  childColumns?: Column<C>[];
  childLoader?: React.FC<{ count: number }>;
  fetchChildRows?: (parentRow: T) => Promise<C[]>;
  fetchMoreChildRows?: (parentRow: T, currentPage: number) => Promise<C[]>;
  renderChildRow?: (child: T, parent: T) => React.ReactNode;
  getChildLoaderCount?: (rowData: T, currentPage: number) => number;
  hierarchyId?: string;
  childGridPageSize?: number;
}) => {
  const { t } = useTranslation();
  const [lastRowRef, setLastRowRef] = useState<HTMLTableRowElement | null>(
    null,
  );

  const rowId = parentRow[hierarchyId as keyof T] as string;

  // Use React Query for child data with unique cache key
  const {
    data: childData,
    isLoading: isChildLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: childError,
    isError: isChildError,
    status,
  } = useInfiniteQuery(
    ['child-data', rowId],
    async ({ pageParam = 1 }) => {
      try {
        if (pageParam === 1 && fetchChildRows) {
          const data = await fetchChildRows(parentRow);
          return { data, hasNextPage: data.length === childGridPageSize };
        } else if (fetchMoreChildRows) {
          const data = await fetchMoreChildRows(parentRow, pageParam);
          return { data, hasNextPage: data.length === childGridPageSize };
        }

        return { data: [], hasNextPage: false };
      } catch (error) {
        console.error('Child data fetch error:', error);
        throw error;
      }
    },
    {
      enabled: !!parentRow && !!rowId,
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.hasNextPage ? allPages.length + 1 : undefined;
      },
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Unused data is kept in cache for 10 minutes
      retry: (failureCount, error) => {
        // Only retry if the error is NOT 401 (Unauthorized)
        if ((error as any).response?.status === 401) {
          return false;
        }
        // Reduce retry attempts to see errors faster
        return failureCount < 1; // Only retry once
      },
      retryDelay: 1000, // 1 second delay between retries
    },
  );

  const currentPage = childData?.pages?.length || 1;

  const dynamicLoaderCount = useMemo(() => {
    if (getChildLoaderCount) {
      return getChildLoaderCount(parentRow, currentPage);
    }
    return 5; // Default loader count
  }, [getChildLoaderCount, parentRow, currentPage]);

  // Intersection Observer for pagination when last row comes into view
  useEffect(() => {
    if (!lastRowRef || !hasNextPage || isFetchingNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: '100px', // Trigger 100px before the element enters viewport
        threshold: 0.1, // Trigger when 10% of the element is visible
      },
    );

    observer.observe(lastRowRef);

    return () => {
      observer.disconnect();
    };
  }, [lastRowRef, hasNextPage, isFetchingNextPage, fetchNextPage, rowId]);

  const rows: JSX.Element[] = [];

  const hasError =
    isChildError ||
    childError ||
    (childData && childData.pages.length === 0 && !isChildLoading);

  if (hasError) {
    rows.push(
      <tr key={`error-${rowId}`}>
        <td
          colSpan={columns.length}
          className="p-4 text-left font-inter border border-red-200"
        >
          <div className="flex items-center min-h-[40px] ml-7 relative">
            <div
              className="absolute left-[-6px] top-[-8px] z-[10]"
              style={{ pointerEvents: 'none' }}
            >
              <BranchStartIcon />
            </div>
            <div className="flex items-center space-x-2 pl-2">
              <span className="text-sm font-medium">
                {t('analytics.error-screen-message')}
              </span>
              <Button
                onClick={() => window.location.reload()}
                pattern="outline"
                kind="secondary"
                size="small"
                className="border-neutral-b4 !rounded-lg !w-fit outline-none focus-visible:border-blue-500 focus-visible:outline focus-visible:ring-1 focus-visible:outline-1 focus-visible:outline-blue-500 focus-visible:outline-offset-0"
              >
                <span className={'font-medium'}>{t('analytics.refresh')}</span>
              </Button>
            </div>
          </div>
        </td>
      </tr>,
    );
  } else if (isChildLoading && !childData) {
    if (childLoader) {
      const ChildLoader = childLoader;
      rows.push(
        <React.Fragment key={`loading-${rowId}`}>
          <ChildLoader count={dynamicLoaderCount} />
        </React.Fragment>,
      );
    }
  } else if (childData) {
    const allChildData = childData.pages.flatMap(page => page.data || []);

    if (renderChildRow) {
      allChildData.forEach((child, childIndex) => {
        const isLastRow = childIndex === allChildData.length - 1;
        rows.push(
          <tr
            key={`child-${rowId}-${childIndex}`}
            ref={isLastRow ? setLastRowRef : null}
          >
            <td colSpan={columns.length} className="p-0">
              {renderChildRow(child as any, parentRow)}
            </td>
          </tr>,
        );
      });
    } else if (childColumns) {
      allChildData.forEach((child, childIndex) => {
        const isLastRow = childIndex === allChildData.length - 1;

        if (isLastRow) {
          rows.push(
            <LastDataRow
              key={`child-${rowId}-${childIndex}`}
              rowIndex={childIndex}
              columns={childColumns as Column<any>[]}
              rowData={child}
              isChild={true}
              ref={setLastRowRef}
            />,
          );
        } else {
          rows.push(
            <DataRow
              key={`child-${rowId}-${childIndex}`}
              rowIndex={childIndex}
              columns={childColumns as Column<any>[]}
              rowData={child}
              isChild={true}
            />,
          );
        }
      });

      // Show pagination loading state for child grid if applicable
      if (isFetchingNextPage && childLoader) {
        const ChildLoader = childLoader;
        rows.push(
          <React.Fragment key={`child-loading-more-${rowId}`}>
            <ChildLoader count={dynamicLoaderCount} />
          </React.Fragment>,
        );
      }
    }
  }

  return <>{rows}</>;
};

export const DataGrid = <T, C = T>({
  columns,
  data,
  onSort,
  loader,
  showLoader,
  loaderCount,
  fetchMore,
  sortConfig,
  noResults,
  error,
  isError,
  hierarchyId,
  fetchChildRows,
  fetchMoreChildRows,
  childColumns,
  childLoader,
  renderChildRow,
  onExpandToggle,
  childGridPageSize = 10,
  getChildLoaderCount,
  disableTriStateSort = false,
  onExpandCollapse,
}: DataGridProps<T, C>): JSX.Element => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleScroll = useThrottledCallback(() => {
    // Check if the user has scrolled to the bottom of the page
    if (
      fetchMore &&
      window.innerHeight + window.scrollY >= document.body.scrollHeight - 5
    ) {
      fetchMore(); // Call fetchMore when at the bottom
    }
  }, 300); // Throttle delay in milliseconds

  useEffect(() => {
    // Attach scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const hasVerticalScrollbar =
      document.documentElement.scrollHeight > window.innerHeight;
    if (!hasVerticalScrollbar) {
      fetchMore && fetchMore();
    }
  }, [fetchMore]);

  const handleSort = useCallback(
    (columnKey: keyof T) => {
      let direction: 'asc' | 'desc' | '' = 'asc';
      if (sortConfig.name === columnKey) {
        if (sortConfig.direction === 'asc') {
          direction = 'desc';
        } else if (!disableTriStateSort && sortConfig.direction === 'desc') {
          direction = '';
        } else if (disableTriStateSort && sortConfig.direction === 'desc') {
          direction = 'asc';
        }
      }
      onSort && onSort(columnKey, direction);
    },
    [onSort, sortConfig.direction, sortConfig.name, disableTriStateSort],
  );

  const toggleRow = (row: T) => {
    const rowId = row[hierarchyId as keyof T] as string;

    const isExpanded = expandedRows.has(rowId);

    onExpandCollapse?.(row, isExpanded);

    if (isExpanded) {
      setExpandedRows(prev => {
        const updated = new Set(prev);
        updated.delete(rowId);
        return updated;
      });
    } else {
      setExpandedRows(prev => new Set(prev).add(rowId));
    }

    onExpandToggle?.(row);
  };

  const Loader: React.FC<{ count: number }> = loader;
  const NoResults = noResults() as React.FC;
  const Error: React.FC = error;

  const renderRows = () => {
    const rows: JSX.Element[] = [];

    data.forEach((row, rowIndex) => {
      const rowId = hierarchyId
        ? String(row[hierarchyId as keyof T])
        : (row as { id?: string }).id ?? String(rowIndex);

      const isExpanded = expandedRows.has(rowId);

      // Render parent row
      rows.push(
        <DataRow
          rowIndex={rowIndex}
          key={`parent-${rowId}`}
          columns={columns}
          rowData={row}
          onToggleExpand={hierarchyId ? toggleRow : undefined}
          isExpanded={isExpanded}
        />,
      );

      // Render child rows if expanded
      if (hierarchyId && isExpanded) {
        rows.push(
          <ChildRowRenderer
            key={`child-container-${rowId}`}
            parentRow={row}
            rowIndex={rowIndex}
            columns={columns}
            childColumns={childColumns}
            childLoader={childLoader}
            fetchChildRows={fetchChildRows}
            fetchMoreChildRows={fetchMoreChildRows}
            renderChildRow={renderChildRow}
            getChildLoaderCount={getChildLoaderCount}
            hierarchyId={hierarchyId}
            childGridPageSize={childGridPageSize}
          />,
        );
      }
    });

    return rows;
  };

  return (
    <div className="w-full flex flex-col overflow-hidden">
      <table
        className="table-fixed w-full border-collapse"
        style={{ overflow: 'visible' }}
      >
        <colgroup>
          {columns.map((col, index) => (
            <col key={index} style={{ width: col.width || 'auto' }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <TH key={index} width={column.width} sort={column.sort}>
                <button
                  className={`flex w-full ${
                    column.sort === false ? 'cursor-default' : 'cursor-pointer'
                  } border-box focus:outline-none focus:ring-0 active:outline-none active:border-none transition-none items-center overflow-hidden`}
                  onClick={() =>
                    column.name &&
                    column.sort !== false &&
                    handleSort(column.name)
                  }
                  tabIndex={-1}
                  title={column.text}
                >
                  <span className={`${InterHeaderStyles}`}>{column.text}</span>
                  <span className="ml-2 flex-shrink-0 w-6 h-6">
                    {!!onSort &&
                      column.sort !== false &&
                      sortConfig.name === column.name &&
                      sortConfig.direction &&
                      (sortConfig.direction === 'asc' ? (
                        <SortAscIcon />
                      ) : (
                        <SortDescIcon />
                      ))}
                  </span>
                </button>
              </TH>
            ))}
          </tr>
        </thead>
        <tbody style={{ overflow: 'visible' }}>
          {data.length === 0 && !showLoader && !isError && (
            <tr>
              <td colSpan={columns.length} className="mt-6">
                <NoResults />
              </td>
            </tr>
          )}
          {!!data?.length && renderRows()}
          {showLoader && <Loader count={loaderCount} />}
          {isError && (
            <tr>
              <td colSpan={columns.length} className="mt-6">
                <Error />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
